import React, { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../../AppContext";
import { formatFileSize, formatNumber } from "../../../helpers/common.helper";
import { ReactComponent as DownloadSvg } from "../../../assets/icons/tendering/download.svg";
import { ReactComponent as FileSvg } from "../../../assets/icons/tendering/document.svg";
import { ReactComponent as ArrowRightSvg } from "../../../assets/workExchange/arrow-right-circle.svg";
import { ReactComponent as ArrowLeftSvg } from "../../../assets/workExchange/arrow-left-circle.svg";
import { ReactComponent as ShareSvg } from "../../../assets/workExchange/icon-share.svg";
import { ReactComponent as GoogleMapSvg } from "../../../assets/tendering/location-marker.svg";
import { ReactComponent as InfoSvg } from "../../../assets/tendering/info.svg";
import { ReactComponent as TrashSvg } from "../../../assets/tendering/trash.svg";
import { ReactComponent as EditSvg } from "../../../assets/tendering/edit.svg";
import { ReactComponent as WarningSvg } from "../../../assets/icons/warning.svg";
import { ReactComponent as DocumentSvg } from "../../../assets/tendering/document.svg";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { endOfDay, format } from "date-fns";
import useUsers from "../../../hooks/useUsers";
import SharePopup from "../../../components/Tendering/SharePopup";
import Button from "../../../components/common/Button";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Participants from "./Participants";
import useTendering from "../../../hooks/useTendering";
import useCompanies from "../../../hooks/useCompanies";
import Quotations from "./Quotations";
import Overlay from "../../../components/Overlay";
import _ from "lodash";
import TenderingDetailContext from "../context";
import { SocketContext } from "../../../contexts/SocketContext";
import { ObjectId } from "bson";
import useQuotation from "../../../hooks/useQuotation";

const assetsUri = import.meta.env.ASSETS_URI;

const TaxRequirements = {
  vat: "Vat 7%",
  tax: "Tax 3%",
  none: "None",
};

const Detail = () => {
  const {
    details,
    setDetails,
    projectState,
    setProjectState,
    togglePayment,
    currentQuotation,
    paymentTerms,
  } = useContext(TenderingDetailContext);
  const {
    isAuth,
    user,
    designStyles,
    displayImages,
    toggleAuthModal,
    tenderingViewNDA,
  } = useContext(AppContext);
  const { socket } = useContext(SocketContext);
  const { queryUsers } = useUsers();
  const { queryCompany } = useCompanies();
  const {
    getById,
    participate,
    deleteById,
    signNDA,
    submitSchedule,
    getSignedUrl,
  } = useTendering();
  const scheduleRef = useRef(null);
  const [owner, setOwner] = useState(undefined);
  const [isToggleParticipants, setIsToggleParticipants] = useState(undefined);
  const [isToggleQuotations, setIsToggleQuotations] = useState(undefined);
  const [isPopupShareModal, setIsPopupShareModal] = useState(false);
  const [isToggleDelete, setIsToggleDelete] = useState(undefined);
  const navigate = useNavigate();
  const [overallStatus, setOverallStatus] = useState(undefined);
  const [participants, setParticipants] = useState(details.participants ?? []);
  const [projectStatus, setProjectStatus] = useState(details.status);

  const {
    _id,
    name,
    desc,
    period,
    work,
    space,
    styles,
    location,
    googleMap,
    duration,
    area,
    budget,
    bankGuarantee,
    taxRequirement,
    images,
    documents,
    type,
    createdBy,
    category,
    retention,
    contract,
    contractor,
  } = details;

  const { start: startDate, end: endDate } = period ?? {};
  const { value: durationValue, unit: durationUnit } = duration ?? {};
  const { size: areaSize, unit: areaUnit } = area ?? {};

  const {
    isOwner,
    isOpen,
    canEdit,
    participation,
    selectedForTendering,
    isContractor,
  } = projectState;

  // * Get Design styles
  const { cover, gallery, moodAndTones, renderings, finalImages } = images;

  const { drawings, miscellaneous, perspectiveRenders } = documents ?? {};
  const _renderInfoName = (name, infoIcon) => {
    return (
      <span className="tendering-detail__info__name">
        {name}
        {infoIcon === true && <InfoSvg width={18} />}
      </span>
    );
  };

  const _renderInfoValue = (value) => {
    return <span className="tendering-detail__info__value">{value}</span>;
  };

  const _getOwner = () => {
    if (!owner) {
      return <></>;
    }

    return (
      <div className="tendering-detail__owner">
        <img
          className="tendering-detail__owner__avatar"
          src={`${assetsUri}/${owner.avatar}`}
          onClick={() => {
            if (owner.isCompany) {
              navigate(`/companies/${owner.uri}`);
              return;
            }

            navigate(`/${owner.uri}`);
          }}
        />
        <span
          className="tendering-detail__owner__name"
          onClick={() => {
            if (owner.isCompany) {
              navigate(`/companies/${owner.uri}`);
              return;
            }

            navigate(`/${owner.uri}`);
          }}
        >
          {owner.name}
        </span>
      </div>
    );
  };

  const _getImageUri = (path) => {
    if (!path) return path;

    if (path.startsWith("blob:")) {
      return path;
    }

    return `${assetsUri}/${path}`;
  };

  useEffect(() => {
    if (!details.createdBy) return;

    if (details.company) {
      queryCompany({ _id: details.company }, 1, 1, {
        logo: 1,
        name: 1,
      }).then(({ data: companies }) => {
        setOwner({
          isCompany: true,
          uri: companies[0]._id,
          avatar: companies[0].logo,
          name: companies[0].name,
        });
      });
    }

    if (!details.company) {
      // * Get user data
      queryUsers({ _id: createdBy }, 1, 1, {
        avatar: 1,
        firstname: 1,
        surname: 1,
        uri: 1,
      }).then(({ data: users }) => {
        // TODO: Handle if user not found
        if (!users?.length) {
          return;
        }

        setOwner({
          isCompany: false,
          uri: users[0].uri,
          avatar: users[0].avatar,
          name: users[0].firstname + " " + users[0].surname ?? "",
        });
      });
    }

    const _status = _getOverallStatus();
    setOverallStatus(_status);

    setParticipants(details.participants ?? []);
    setProjectStatus(details.status);

    const _isOwner = createdBy === user?._id;
    const _isOpen =
      new Date() > new Date(period.start) &&
      new Date() < endOfDay(new Date(period.end));
    const _canEdit = _isOwner && !_isOpen;

    const _participation = details.participants?.find(
      (p) => p.user === user?._id
    );
    const _selectedForTendering = _participation?.accepted === true;

    const _isContractor = _participation?.won ?? false;

    setProjectState({
      isOwner: _isOwner,
      isOpen: _isOpen,
      canEdit: _canEdit,
      participation: _participation,
      selectedForTendering: _selectedForTendering,
      isContractor: _isContractor,
    });
  }, [details]);

  useEffect(() => {
    setOverallStatus(_getOverallStatus());
  }, [projectStatus]);

  const _getOverallStatus = () => {
    // * Handle same status display for both
    if (projectStatus === 2) {
      return {
        label: "Tendering",
        color: "#A66A01",
        background: "#FFDB4F",
      };
    }

    if (projectStatus === 3) {
      return {
        label: "Contract Signing",
        color: "#A66A01",
        background: "#FFDB4F",
      };
    }

    if (projectStatus === 4) {
      return {
        label: "Project in progress",
        color: "#A66A01",
        background: "#FFDB4F",
      };
    }

    // * Handle is owner
    if (isOwner) {
      if (projectStatus === 1) {
        if (!isOpen) {
          return {
            label: "Announcement",
            color: "#fff",
            background: "#0098ff",
          };
        }

        if (isOpen) {
          return {
            label: "Open",
            color: "#fff",
            background: "#14D886",
          };
        }
      }

      return {
        label: "XXX",
        color: "#A66A01",
        background: "#FFDB4F",
      };
    }

    // * Handle for participant
    if (projectStatus === 1) {
      if (!isOpen) {
        return {
          label: "Announcement",
          color: "#fff",
          background: "#0098ff",
        };
      }

      if (isOpen) {
        return {
          label: "Open",
          color: "#fff",
          background: "#14D886",
        };
      }
    }

    return {
      label: "XXX",
      color: "#A66A01",
      background: "#FFDB4F",
    };
  };

  const _getMainAction = () => {
    // * Handle is owner
    if (isOwner) {
      if (projectStatus === 1 || projectStatus === 2) {
        let buttonLabel = "Participants";

        if (projectStatus === 2 && category === 1)
          buttonLabel = "View Quotations";
        if (projectStatus === 2 && category === 2)
          buttonLabel = "View Quotations and BOQs";

        return (
          <Button
            label={buttonLabel}
            size="small"
            disabled={!isOpen}
            onClick={() => {
              if (!isOpen) {
                toast.error("Must wait until Tendering is open!", {
                  position: "top-right",
                });
                return;
              }

              if (projectStatus === 1) {
                setIsToggleParticipants(true);
                return;
              }

              setIsToggleQuotations(true);
            }}
          />
        );
      }

      if (projectStatus === 3 && _.isEmpty(contract)) {
        return (
          <Button
            label={"Create contract"}
            size="small"
            onClick={() => {
              navigate("contract/owner");
            }}
          />
        );
      }

      if (projectStatus === 3 && contract.status === 1) {
        return (
          <Button
            label={"Waiting contract to be signed"}
            size="small"
            disabled={true}
          />
        );
      }

      if (projectStatus === 4 && currentQuotation && paymentTerms?.length > 0) {
        // * Check if payments has been paid
        if (!paymentTerms[0].paid) {
          // * Get advanced and 1st payment price
          const _advanced = paymentTerms[0].total;
          const _firstPayment = paymentTerms[1].subTotal;

          const isWht = currentQuotation.wht < 0;

          const _total =
            (_advanced + _firstPayment) * 1.07 * (isWht ? 0.97 : 1);

          return (
            <Button
              label={"Pay advanced payment"}
              size="small"
              onClick={() => {
                togglePayment(_total);
              }}
            />
          );
        }
      }
    }

    // * Handle if a contractor
    if (projectStatus === 1) {
      if (!participation) {
        return (
          <Button
            label="Participate"
            size="small"
            disabled={!isOpen}
            onClick={async () => {
              if (!isOpen) {
                toast.error("Must wait until Tendering is open!", {
                  position: "top-right",
                });
                return;
              }

              if (!isAuth) {
                toggleAuthModal(
                  true,
                  undefined,
                  `/tenderings/${_id}/participate`
                );
                return;
              }

              try {
                await participate(_id);
                toast.success("Participated successfully");
                refetchDetail();
              } catch (error) {
                toast.error("Unable to participate");
              }
            }}
          />
        );
      }

      if (!participation.nda.sent) {
        return <Button label="Waiting for NDA" size="small" disabled={true} />;
      }

      if (!participation.nda.signed) {
        return (
          <Button
            label="View & Sign NDA"
            size="small"
            onClick={() => {
              tenderingViewNDA(_id);
            }}
          />
        );
      }

      if (participation.nda.signed) {
        // TODO: What to show
        return (
          <Button
            label="Waiting owner to select participants"
            size="small"
            disabled={true}
          />
        );
      }
    }

    if (!selectedForTendering) {
      return null;
    }

    if (projectStatus === 2) {
      return (
        <>
          <input
            ref={scheduleRef}
            type="file"
            name="scheduleFile"
            multiple={false}
            style={{ display: "none" }}
            onChange={handleScheduleFileChange}
          />
          {!participation.quotation.requested && (
            <Button
              label="Send New Quotation"
              variant={"secondary"}
              size="small"
              onClick={async () => {
                navigate("quotation");
              }}
            />
          )}
          {participation.quotation.requested && (
            <Button
              label="Send Quotation"
              size="small"
              onClick={async () => {
                navigate("quotation");
              }}
            />
          )}
          {!participation.schedule.requested && (
            <Button
              label="Upload new Master Schedule"
              variant={"secondary"}
              size="small"
              onClick={async () => {
                scheduleRef?.current.click();
              }}
            />
          )}
          {participation.schedule.requested && (
            <Button
              label="Upload Master Schedule"
              size="small"
              onClick={async () => {
                scheduleRef?.current.click();
              }}
            />
          )}
        </>
      );
    }

    if (!isContractor) {
      return;
    }

    if (projectStatus === 3 && _.isEmpty(contract)) {
      return (
        <Button label={"Waiting for contract"} size="small" disabled={true} />
      );
    }

    if (projectStatus === 3 && contract.status === 1) {
      return (
        <Button
          label={"Sign contract"}
          size="small"
          onClick={() => navigate("contract/contractor")}
        />
      );
    }

    if (projectStatus === 4 && currentQuotation) {
      // * Check if payments has been paid
      if (!currentQuotation.advancedPayment.paid) {
        return (
          <Button label={"Waiting payment"} size="small" disabled={true} />
        );
      }
    }
  };

  const _getModalDelete = () => {
    // * Get work from datasource
    const prefix = "profile-project-owner";

    return (
      <>
        <Overlay
          onClose={() => {
            setIsToggleDelete(false);
          }}
          style={{
            alignItems: "flex-start",
          }}
        >
          <div className={`${prefix}-delete`}>
            <WarningSvg width={64} />
            <p className={`${prefix}-delete__header`}>{`Delete ${name}?`}</p>
            <div className={`${prefix}-delete__action`}>
              <Button
                variant={"secondary"}
                label={"Cancel"}
                onClick={() => setIsToggleDelete(undefined)}
              />
              <Button
                label={"Confirm"}
                onClick={async () => {
                  await deleteById(details._id);
                  toast.warn("Tendering project deleted");

                  if (owner.isCompany) {
                    navigate(`/companies/${owner.uri}`);
                    return;
                  }

                  navigate(`/${owner.uri}`);
                }}
              />
            </div>
          </div>
        </Overlay>
      </>
    );
  };

  const refetchDetail = () => {
    // * Refetch data
    getById(_id).then((response) => {
      setDetails(response);
    });
  };

  const _uploadFile = async (key, file) => {
    const signedUrl = await getSignedUrl(key, file.type);

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadResponse.status !== 200) {
      return false;
    }

    return true;
  };

  const handleScheduleFileChange = async ({ target: { files } }) => {
    if (files.length === 0) {
      scheduleRef.current.value = null;
      return;
    }

    const _file = files[0];

    if (!window.confirm(`Upload ${_file.name}?`)) {
      scheduleRef.current.value = null;
      return;
    }

    await toast.promise(
      new Promise(async (resolve, reject) => {
        const scheduleId = new ObjectId();

        const originalKey = `tenderings/${_id}/participants/${user._id}/schedules/${scheduleId}`;

        if (!(await _uploadFile(originalKey, _file))) {
          reject("Failed");
          return;
        }

        await submitSchedule(_id, user._id, {
          _id: scheduleId,
          key: originalKey,
          name: _file.name,
          mimeType: _file.type,
          size: _file.size,
        });

        resolve();
      }),
      {
        pending: "Uploading schedule",
        success: "Master schedule submitted",
        error: "Failed to submit schedule",
      }
    );

    scheduleRef.current.value = null;
  };

  const handleDownloadFiles = async (file) => {
    const { name, key } = file;

    const path = `${assetsUri}/${key}`;

    await toast.promise(
      new Promise(async (resolve, reject) => {
        const res = await fetch(path, { cache: "no-cache" });
        const blob = await res.blob();

        saveAs(blob, name);

        resolve();
      }),
      {
        pending: "Downloading",
        success: "File downloaded",
        error: "Failed to download",
      },
      {
        autoClose: 1000,
      }
    );
  };

  useEffect(() => {
    if (!socket) return;

    socket.on("tenderings:project-updated", (payload) => {
      if (!payload) return;

      const { _id } = payload;
      if (!_id) return;

      if (_id !== details._id) return;

      refetchDetail();
    });

    return () => {
      socket.removeAllListeners("tenderings:project-updated");
    };
  }, [socket]);

  let displayDocuments = false;

  if (isOwner) displayDocuments = true;
  if (!isOwner && participation?.accepted) displayDocuments = true;

  return (
    <div className="tendering-detail">
      <div className="tendering-detail__container">
        <div className="tendering-detail__header">
          {overallStatus && (
            <div
              className="tendering-detail__header__status"
              style={{
                color: overallStatus.color,
                background: overallStatus.background,
              }}
            >
              {overallStatus.label}
            </div>
          )}
          <div className="tendering-detail__header__actions">
            {projectStatus >= 4 &&
              currentQuotation?.advancedPayment?.paid &&
              (isOwner || isContractor) && (
                <DocumentSvg
                  className="tendering-detail__header__actions__action"
                  width={18}
                  onClick={() => {
                    navigate("documents/quotations");
                  }}
                  style={{ marginRight: 3 }}
                />
              )}
            <ShareSvg
              className="tendering-detail__header__actions__action"
              data-tooltip-id="tooltip-detail"
              data-tooltip-content={"Share"}
              data-tooltip-place="top"
              width={18}
              onClick={() => setIsPopupShareModal(true)}
            />
            {googleMap && (
              <GoogleMapSvg
                className="tendering-detail__header__actions__action"
                data-tooltip-id="tooltip-detail"
                data-tooltip-content={"Share"}
                data-tooltip-place="top"
                width={19}
                onClick={() => window.open(googleMap)}
              />
            )}
            {isOwner && (
              <>
                <EditSvg
                  className="tendering-detail__header__actions__action"
                  data-state={canEdit ? "enable" : "disable"}
                  data-tooltip-id="tooltip-detail"
                  data-tooltip-content={"Share"}
                  data-tooltip-place="top"
                  width={20}
                  onClick={() => navigate("edit")}
                />
                <TrashSvg
                  className="tendering-detail__header__actions__action"
                  data-state={canEdit ? "enable" : "disable"}
                  data-tooltip-id="tooltip-detail"
                  data-tooltip-content={"Share"}
                  data-tooltip-place="top"
                  width={20}
                  onClick={() => setIsToggleDelete(true)}
                />
              </>
            )}
          </div>
        </div>
        <h3 className="tendering-detail__title">{name}</h3>
        <div className="tendering-detail__action">{_getMainAction()}</div>
        {_getOwner()}
        <div className="tendering-detail__dates">
          <span className="tendering-detail__dates__title">Tender Date</span>
          <span>
            {format(new Date(startDate), "dd MMM yyyy")}
            {" - "}
            {format(new Date(endDate), "dd MMM yyyy")}
          </span>
        </div>
        <img
          className="tendering-detail__cover"
          src={_getImageUri(cover.cropped ?? cover.original)}
        />
        {gallery?.length > 0 && (
          <div className="tendering-detail__gallery">
            <Swiper
              className="tendering-detail__gallery__swiper"
              modules={[Navigation, Pagination]}
              spaceBetween={12}
              slidesPerView={3}
              slidesPerGroup={3}
              loop={true}
              navigation={{
                enabled: true,
                prevEl: ".tendering-detail__gallery__controls__arrows--prev",
                nextEl: ".tendering-detail__gallery__controls__arrows--next",
              }}
              pagination={{
                clickable: true,
                el: ".tendering-detail__gallery__controls__pagination",
                bulletClass:
                  "tendering-detail__gallery__controls__pagination__bullet",
                bulletActiveClass:
                  "tendering-detail__gallery__controls__pagination__bullet--active",
                renderBullet: function (index, className) {
                  return '<span class="' + className + '">' + "</span>";
                },
              }}
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  slidesPerGroup: 1,
                },
                768: {
                  slidesPerView: 2,
                  slidesPerGroup: 2,
                },
                1200: {
                  slidesPerView: 3,
                  slidesPerGroup: 3,
                },
              }}
            >
              {gallery.map((image, index) => {
                return (
                  <SwiperSlide
                    className="tendering-detail__gallery__swiper__slide"
                    onClick={() => {
                      displayImages(
                        gallery.map((image) => image.original),
                        index
                      );
                    }}
                  >
                    <img
                      className="tendering-detail__gallery__swiper__slide__image"
                      src={_getImageUri(image.cropped ?? image.original)}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="tendering-detail__gallery__controls">
              <ArrowLeftSvg
                className="tendering-detail__gallery__controls__arrows tendering-detail__gallery__controls__arrows--prev"
                width={32}
              />
              <div className="tendering-detail__gallery__controls__pagination" />
              <ArrowRightSvg
                className="tendering-detail__gallery__controls__arrows tendering-detail__gallery__controls__arrows--next"
                width={32}
              />
            </div>
          </div>
        )}
        <div className="tendering-detail__desc">
          <p className="tendering-detail__desc__text">{desc}</p>
        </div>
        <div className="tendering-detail__info">
          <h4 className="tendering-detail__info__title">Information</h4>
          {_renderInfoName("Project type")}
          {_renderInfoValue(category === 1 ? "Design" : "Construction")}
          {_renderInfoName("Type of work")}
          {_renderInfoValue(
            <div className="tendering-detail__info__tag--work">
              {designStyles.works.find((w) => w.value === work)?.name}
            </div>
          )}
          {_renderInfoName("Type of space")}
          {_renderInfoValue(
            <div className="tendering-detail__info__tag--space">
              {designStyles.spaces.find((w) => w.value === space)?.name}
            </div>
          )}
          {_renderInfoName("Type of styles")}
          {_renderInfoValue(
            styles.map((tos) => (
              <div className="tendering-detail__info__tag">
                {designStyles.styles.find((w) => w.value === tos)?.name}
              </div>
            ))
          )}
          {_renderInfoName("Area")}
          {_renderInfoValue(`${areaSize} ${areaUnit}`)}
          {_renderInfoName("Location")}
          {_renderInfoValue(location)}
          {_renderInfoName("Duration")}
          {_renderInfoValue(`${durationValue} ${durationUnit}`)}
          {_renderInfoName("Tax requirements")}
          {_renderInfoValue(TaxRequirements[taxRequirement])}
          {bankGuarantee && (
            <>
              {_renderInfoName("Bank Guarantee")}
              {_renderInfoValue(`${bankGuarantee}%`)}
            </>
          )}
          {_renderInfoName("Budget")}
          {_renderInfoValue(formatNumber(budget, "THB"))}
          {category === 2 && (
            <>
              {_renderInfoName("Security Deposit", true)}
              {_renderInfoValue(retention === true ? "Yes" : "No")}
            </>
          )}
        </div>
        <div className="tendering-detail__other">
          <div className="tendering-detail__other__header">
            <h4 className="tendering-detail__other__title">
              {category === 1 ? "Mood & Tone" : "3D Renderings"}
            </h4>
            <div className="tendering-detail__other__gallery__controls">
              <ArrowLeftSvg
                className="tendering-detail__other__gallery__controls__arrows tendering-detail__other__gallery__controls__arrows--prev"
                width={32}
              />
              <ArrowRightSvg
                className="tendering-detail__other__gallery__controls__arrows tendering-detail__other__gallery__controls__arrows--next"
                width={32}
              />
            </div>
          </div>
          <div className="tendering-detail__other__gallery">
            <Swiper
              className="tendering-detail__other__gallery__swiper"
              modules={[Navigation, Pagination]}
              spaceBetween={12}
              slidesPerView={2}
              slidesPerGroup={2}
              loop={true}
              navigation={{
                enabled: true,
                prevEl:
                  ".tendering-detail__other__gallery__controls__arrows--prev",
                nextEl:
                  ".tendering-detail__other__gallery__controls__arrows--next",
              }}
              pagination={{
                clickable: true,
                el: ".tendering-detail__other__gallery__controls__pagination",
                bulletClass:
                  "tendering-detail__other__gallery__controls__pagination__bullet",
                bulletActiveClass:
                  "tendering-detail__other__gallery__controls__pagination__bullet--active",
                renderBullet: function (index, className) {
                  return '<span class="' + className + '">' + "</span>";
                },
              }}
              breakpoints={{
                768: {
                  slidesPerView: 3,
                  slidesPerGroup: 3,
                },
                1024: {
                  slidesPerView: 1,
                  slidesPerGroup: 1,
                },
              }}
            >
              {((category === 1 ? moodAndTones : renderings) ?? []).map(
                (image, index) => {
                  return (
                    <SwiperSlide
                      className="tendering-detail__other__gallery__swiper__slide"
                      onClick={() => {
                        displayImages(
                          (category === 1 ? moodAndTones : renderings).map(
                            (image) => image.original
                          ),
                          index
                        );
                      }}
                    >
                      <img
                        className="tendering-detail__other__gallery__swiper__slide__image"
                        src={_getImageUri(image.cropped ?? image.original)}
                      />
                    </SwiperSlide>
                  );
                }
              )}
            </Swiper>
            <div className="tendering-detail__other__gallery__controls">
              <div className="tendering-detail__other__gallery__controls__pagination" />
            </div>
          </div>
        </div>
        {displayDocuments &&
          (drawings?.length > 0 ||
            miscellaneous?.length > 0 ||
            (category === 2 && perspectiveRenders?.length > 0)) && (
            <div className="tendering-detail__documents">
              <h4 className="tendering-detail__documents__title">Documents</h4>
              {drawings?.length > 0 && (
                <>
                  <div className="tendering-detail__documents__section">
                    <span className="tendering-detail__documents__section__title">
                      Drawings
                    </span>
                    <div className="tendering-detail__documents__section__files">
                      {drawings.map((file) => (
                        <div className="tendering-detail__documents__section__files__file">
                          <div className="tendering-detail__documents__section__files__file__icon">
                            <FileSvg width={32} />
                          </div>
                          <span className="tendering-detail__documents__section__files__file__name">
                            {file.name}
                          </span>
                          <span className="tendering-detail__documents__section__files__file__size">
                            {formatFileSize(file.size)}
                          </span>
                          <div
                            className="tendering-detail__documents__section__files__file__download"
                            onClick={() => handleDownloadFiles(file)}
                          >
                            <DownloadSvg width={32} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="tendering-detail__documents__separator" />
                </>
              )}
              {category === 2 && perspectiveRenders?.length > 0 && (
                <>
                  <div className="tendering-detail__documents__section">
                    <span className="tendering-detail__documents__section__title">
                      Perspective Renders
                    </span>
                    <div className="tendering-detail__documents__section__files">
                      {perspectiveRenders.map((file) => (
                        <div className="tendering-detail__documents__section__files__file">
                          <div className="tendering-detail__documents__section__files__file__icon">
                            <FileSvg width={32} />
                          </div>
                          <span className="tendering-detail__documents__section__files__file__name">
                            {file.name}
                          </span>
                          <span className="tendering-detail__documents__section__files__file__size">
                            {formatFileSize(file.size)}
                          </span>
                          <div
                            className="tendering-detail__documents__section__files__file__download"
                            onClick={() => handleDownloadFiles(file)}
                          >
                            <DownloadSvg width={32} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="tendering-detail__documents__separator" />
                </>
              )}
              {miscellaneous?.length > 0 && (
                <>
                  <div className="tendering-detail__documents__section">
                    <span className="tendering-detail__documents__section__title">
                      Miscellaneous
                    </span>
                    <div className="tendering-detail__documents__section__files">
                      {miscellaneous.map((file) => (
                        <div className="tendering-detail__documents__section__files__file">
                          <div className="tendering-detail__documents__section__files__file__icon">
                            <FileSvg width={32} />
                          </div>
                          <span className="tendering-detail__documents__section__files__file__name">
                            {file.name}
                          </span>
                          <span className="tendering-detail__documents__section__files__file__size">
                            {formatFileSize(file.size)}
                          </span>
                          <div
                            className="tendering-detail__documents__section__files__file__download"
                            onClick={() => handleDownloadFiles(file)}
                          >
                            <DownloadSvg width={32} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
      </div>
      {isPopupShareModal && (
        <SharePopup onClose={() => setIsPopupShareModal(false)} />
      )}
      {isToggleParticipants && (
        <Participants
          tenderingId={_id}
          participants={participants ?? []}
          onClose={() => setIsToggleParticipants(false)}
          onChange={({ participants: _participants, status: _status }) => {
            if (_participants) {
              setParticipants(_participants);
            }

            if (_status) {
              setProjectStatus(_status);

              // * Refetch info for setting participant data
              getById(_id)
                .then((data) => {
                  setParticipants(data.participants);
                  setIsToggleParticipants(false);
                })
                .catch((error) => {
                  toast.error(error.message);
                });
            }
          }}
        />
      )}
      {isToggleDelete && _getModalDelete()}
      {isToggleQuotations && (
        <Quotations
          tenderingId={_id}
          participants={participants ?? []}
          onClose={() => setIsToggleQuotations(false)}
          onWinnerSelected={() => {
            setIsToggleQuotations(false);
            refetchDetail();
          }}
        />
      )}
    </div>
  );
};

Detail.propTypes = {};

export default Detail;
